import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { clinicAPI } from '../utils/api';
import { toast } from 'sonner';
import { Building, MapPin, FileText, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const ClinicSetup = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    clinic_name: '',
    country: '',
    specialties: [],
    description: '',
    treatments_offered: [],
    price_range: '$$',
  });

  const specialtyOptions = [
    'Hair Transplant',
    'Dental',
    'Aesthetics',
    "Women's Health",
    "Men's Health",
    'Bariatric',
    'Orthopedic',
    'Fertility',
    'Cosmetic Surgery',
  ];

  const priceRanges = ['$', '$$', '$$$'];

  const toggleSpecialty = (specialty) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.includes(specialty)
        ? prev.specialties.filter(s => s !== specialty)
        : [...prev.specialties, specialty],
      treatments_offered: prev.specialties.includes(specialty)
        ? prev.treatments_offered.filter(s => s !== specialty)
        : [...prev.treatments_offered, specialty],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.clinic_name || !formData.country || formData.specialties.length === 0 || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await clinicAPI.createProfile({
        user_id: user.id,
        ...formData,
      });
      toast.success('Clinic profile created successfully!');
      navigate('/clinic/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 py-12 px-4" data-testid="clinic-setup">
      <div className="container-custom max-w-4xl">
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-secondary mb-2">Set Up Your Clinic Profile</h1>
            <p className="text-foreground/70">Tell us about your clinic to start connecting with patients</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Clinic Name */}
            <div data-testid="clinic-name-field">
              <label className="block text-sm font-medium text-foreground mb-2">
                <Building className="w-4 h-4 inline-block mr-2" />
                Clinic Name *
              </label>
              <Input
                value={formData.clinic_name}
                onChange={(e) => setFormData({ ...formData, clinic_name: e.target.value })}
                placeholder="Enter your clinic name"
                className="h-12"
                required
                data-testid="clinic-name-input"
              />
            </div>

            {/* Country */}
            <div data-testid="country-field">
              <label className="block text-sm font-medium text-foreground mb-2">
                <MapPin className="w-4 h-4 inline-block mr-2" />
                Country *
              </label>
              <Input
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                placeholder="Enter country"
                className="h-12"
                required
                data-testid="country-input"
              />
            </div>

            {/* Specialties */}
            <div data-testid="specialties-field">
              <label className="block text-sm font-medium text-foreground mb-2">Specialties *</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {specialtyOptions.map((specialty) => (
                  <button
                    key={specialty}
                    type="button"
                    onClick={() => toggleSpecialty(specialty)}
                    className={`p-3 rounded-lg border-2 text-sm font-medium transition-all hover:-translate-y-0.5 ${
                      formData.specialties.includes(specialty)
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-slate-200 text-foreground hover:border-primary/50'
                    }`}
                    data-testid={`specialty-${specialty}`}
                  >
                    {specialty}
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div data-testid="description-field">
              <label className="block text-sm font-medium text-foreground mb-2">
                <FileText className="w-4 h-4 inline-block mr-2" />
                Description *
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe your clinic, services, and what makes you unique..."
                rows={5}
                required
                data-testid="description-input"
              />
            </div>

            {/* Price Range */}
            <div data-testid="price-range-field">
              <label className="block text-sm font-medium text-foreground mb-2">Price Range</label>
              <div className="flex gap-3">
                {priceRanges.map((range) => (
                  <button
                    key={range}
                    type="button"
                    onClick={() => setFormData({ ...formData, price_range: range })}
                    className={`px-6 py-3 rounded-lg border-2 font-medium transition-all ${
                      formData.price_range === range
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-slate-200 text-foreground hover:border-primary/50'
                    }`}
                    data-testid={`price-range-${range}`}
                  >
                    {range}
                  </button>
                ))}
              </div>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-primary hover:bg-primary/90 rounded-full"
              data-testid="submit-clinic-setup"
            >
              {loading ? 'Creating Profile...' : 'Create Clinic Profile'}
              <CheckCircle className="w-5 h-5 ml-2" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClinicSetup;